import { useRef } from "react";

const RELEASE = ["pointerup", "pointercancel", "touchend", "touchcancel"];

/**
 * holdSelectionUntilRelease stops the browser's own long press for the rest of
 * a touch that has already opened a sheet.
 *
 * A long press on a phone is two gestures racing: this hook's timer at 500 ms,
 * and the browser's, measured at about 650 ms in Chromium, which selects the
 * word under the finger and raises a context menu. The transcript refuses
 * selection — but by the time the browser's gesture lands, what is under the
 * finger is the sheet that just opened, which does not. Android Chrome selected
 * a word of the chat and offered Copy and Web search. Refusing selectstart and
 * contextmenu document-wide until the finger lifts covers whatever opened
 * there, and a deliberate long press on the sheet afterwards still selects.
 */
function holdSelectionUntilRelease() {
  const refuse = (e: Event) => e.preventDefault();
  const release = () => {
    document.removeEventListener("selectstart", refuse, true);
    document.removeEventListener("contextmenu", refuse, true);
    for (const type of RELEASE) document.removeEventListener(type, release, true);
  };
  document.addEventListener("selectstart", refuse, true);
  document.addEventListener("contextmenu", refuse, true);
  for (const type of RELEASE) document.addEventListener(type, release, true);
}

/**
 * usePressGesture separates a tap from a long press.
 *
 * The trap, from the original build (requirements/v0-import.md §8): a long
 * press fires pointerup too, so without a flag the correction sheet opens *and*
 * the tap seeks the audio away from the word being corrected. The press timer
 * sets `handled`, and the tap handler refuses to run when it is set.
 *
 * Movement cancels the gesture, so scrolling a chapter with a finger does not
 * open a sheet under it.
 */
export function usePressGesture({
  onTap,
  onLongPress,
  delayMs = 500,
  moveTolerancePx = 10,
}: {
  onTap: () => void;
  onLongPress?: () => void;
  delayMs?: number;
  moveTolerancePx?: number;
}) {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handled = useRef(false);
  const origin = useRef<{ x: number; y: number } | null>(null);

  const clear = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };

  return {
    onPointerDown: (e: React.PointerEvent) => {
      handled.current = false;
      origin.current = { x: e.clientX, y: e.clientY };
      if (!onLongPress) return;
      clear();
      timer.current = setTimeout(() => {
        handled.current = true;
        holdSelectionUntilRelease();
        onLongPress();
      }, delayMs);
    },
    onPointerMove: (e: React.PointerEvent) => {
      if (!origin.current || !timer.current) return;
      const dx = Math.abs(e.clientX - origin.current.x);
      const dy = Math.abs(e.clientY - origin.current.y);
      if (dx > moveTolerancePx || dy > moveTolerancePx) clear();
    },
    // The tap runs on click, not on pointerup, so that Enter and Space on a
    // focused control take exactly the same path as a finger. A separate
    // keydown handler was a second way to do the same thing, and the two drift.
    onClick: () => {
      clear();
      // The whole point: a long press has already done its work, so the tap
      // must not also fire.
      if (handled.current) return;
      onTap();
    },
    onPointerCancel: clear,
    onPointerLeave: clear,
    // Suppress the native context menu and text selection: both fire on a long
    // press and both fight the correction sheet.
    onContextMenu: (e: React.MouseEvent) => e.preventDefault(),
  };
}
